import React, { useMemo } from "react";

import type { ContinuityDiagnostic, ContinuityStatus, NarrativeSonicsContext } from "./NarrativeSonics.types";

type ContinuityLensProps = {
  context: NarrativeSonicsContext;
  diagnostics: ContinuityDiagnostic[];
  compact?: boolean;
};

function fmt(sec: number | undefined): string {
  const safe = typeof sec === "number" && Number.isFinite(sec) ? Math.max(0, sec) : 0;
  const mm = Math.floor(safe / 60);
  const ss = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
}

function statusMeta(status: ContinuityStatus) {
  switch (status) {
    case "coherent":
      return {
        label: "COHERENT",
        border: "rgba(52,211,153,0.22)",
        background: "rgba(16,185,129,0.14)",
        color: "rgba(209,250,229,0.96)",
      };
    case "abrupt":
      return {
        label: "ABRUPT",
        border: "rgba(251,113,133,0.22)",
        background: "rgba(244,63,94,0.14)",
        color: "rgba(255,228,230,0.96)",
      };
    case "unresolved":
      return {
        label: "UNRES",
        border: "rgba(249,115,22,0.22)",
        background: "rgba(234,88,12,0.14)",
        color: "rgba(255,237,213,0.96)",
      };
    case "mismatched":
      return {
        label: "MISMATCH",
        border: "rgba(192,132,252,0.22)",
        background: "rgba(168,85,247,0.14)",
        color: "rgba(243,232,255,0.96)",
      };
    case "dense":
      return {
        label: "DENSE",
        border: "rgba(250,204,21,0.22)",
        background: "rgba(234,179,8,0.14)",
        color: "rgba(254,249,195,0.96)",
      };
    case "gapped":
      return {
        label: "GAPPED",
        border: "rgba(125,211,252,0.22)",
        background: "rgba(14,165,233,0.14)",
        color: "rgba(224,242,254,0.96)",
      };
    default:
      return {
        label: "INFO",
        border: "rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.06)",
        color: "rgba(255,255,255,0.82)",
      };
  }
}

function StatusChip({ status }: { status: ContinuityStatus }): JSX.Element {
  const meta = statusMeta(status);
  return (
    <span
      className="inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black tracking-[0.16em]"
      style={{ borderColor: meta.border, background: meta.background, color: meta.color }}
    >
      {meta.label}
    </span>
  );
}

export default function ContinuityLens({
  context,
  diagnostics,
  compact = false,
}: ContinuityLensProps): JSX.Element {
  const warnings = useMemo(
    () => diagnostics.filter((diagnostic) => diagnostic.status !== "coherent"),
    [diagnostics],
  );
  const countsByStatus = useMemo(() => {
    const counts = new Map<ContinuityStatus, number>();
    for (const diagnostic of diagnostics) {
      counts.set(diagnostic.status, (counts.get(diagnostic.status) ?? 0) + 1);
    }
    return counts;
  }, [diagnostics]);
  const score = useMemo(() => {
    if (diagnostics.length === 0) return null;
    const total = diagnostics.reduce((sum, diagnostic) => sum + diagnostic.continuityScore, 0);
    return total / diagnostics.length;
  }, [diagnostics]);

  if (compact) {
    return (
      <div className="rounded-2xl border border-amber-300/16 bg-white/[0.04] p-3">
        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-100/72">Continuity</div>
        <div className="mt-2 text-sm text-white/86">{warnings.length} warning{warnings.length === 1 ? "" : "s"}</div>
        <div className="mt-2 text-[12px] text-white/62">Score {score == null ? "--" : score.toFixed(2)}</div>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-2xl border border-amber-300/16 bg-white/[0.04] p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-100/72">Continuity Review</div>
          <div className="mt-1 text-sm text-white/88">
            Review derived transitions across authored ARC and FX regions without mutating timeline truth.
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-white/64">
          <span>{fmt(context.currentTimeSec)} / {fmt(context.durationSec)}</span>
          <span>Score {score == null ? "--" : score.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <StatusChip status="coherent" />
        <StatusChip status="abrupt" />
        <StatusChip status="gapped" />
        <StatusChip status="dense" />
        <StatusChip status="mismatched" />
      </div>

      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-3 rounded-2xl border border-white/10 bg-black/10 p-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Summary</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {(["coherent", "abrupt", "gapped", "dense", "mismatched", "unresolved"] as ContinuityStatus[]).map((status) => {
                const count = countsByStatus.get(status) ?? 0;
                if (count === 0) return null;
                return <StatusChip key={status} status={status} />;
              })}
            </div>
            <div className="mt-2 text-[12px] text-white/68">
              {warnings.length === 0
                ? "No derived continuity warnings. Authored ARC and FX transitions currently read coherent."
                : `${warnings.length} warning${warnings.length === 1 ? "" : "s"} derived from authored ARC and FX transitions.`}
            </div>
          </div>

          <div className="rounded-xl border border-amber-300/14 bg-amber-300/[0.06] px-3 py-2 text-[12px] text-amber-50/80">
            Focus {context.focusClipId || context.focusSourceId || context.focusScope}. Read-model only. No canonical clips, markers, or ranges were mutated.
          </div>
        </div>

        <div className="space-y-2 rounded-2xl border border-white/10 bg-black/10 p-3">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-white/56">Warnings By Time</div>
          {warnings.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.03] px-3 py-4 text-[12px] text-white/60">
              No continuity warnings detected from the current authored ARC and FX state.
            </div>
          ) : (
            warnings.map((diagnostic) => {
              const meta = statusMeta(diagnostic.status);
              return (
                <div
                  key={diagnostic.id}
                  className="rounded-xl border px-3 py-3"
                  style={{ borderColor: meta.border, background: meta.background }}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <StatusChip status={diagnostic.status} />
                    <span className="text-[11px] font-semibold text-white/78">
                      {fmt(diagnostic.startSec)} to {fmt(diagnostic.endSec)}
                    </span>
                  </div>
                  <div className="mt-2 text-[12px] text-white/88">{diagnostic.warning}</div>
                  <div className="mt-2 text-[11px] text-white/62">Score {diagnostic.continuityScore.toFixed(2)}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}